import { Screen, useNavigate } from "flemo";
import { XIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

import { formatTermsDate, TERMS_VERSIONS } from "./_data/versions";

const CONTACT_EMAIL = "support@case-d.com";

export function TermsPage() {
  const { pop } = useNavigate();
  const [versionIndex, setVersionIndex] = useState(0);

  const version = TERMS_VERSIONS[versionIndex];
  const isLatest = versionIndex === 0;
  const nextVersion = isLatest ? null : TERMS_VERSIONS[versionIndex - 1];

  const handleVersionChange = (index: number) => {
    setVersionIndex(index);
    trackEvent("terms_version_changed", { effective_date: TERMS_VERSIONS[index].effectiveDate });
  };

  return (
    <Screen>
      <main className="mx-auto w-full max-w-3xl px-6 pb-16 overflow-y-auto overscroll-none scrollbar-hidden pt-safe">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-4 pt-2">
          <h1 className="text-2xl font-bold">이용약관</h1>

          <Button variant="ghost" size="icon" onClick={pop}>
            <XIcon className="size-5 text-muted-foreground" />
          </Button>
        </div>

        {/* 이전 버전 열람 시 안내 배너 */}
        {!isLatest && nextVersion && (
          <div className="mb-6 rounded-lg border border-border bg-muted/50 p-4 text-sm leading-relaxed">
            <p>
              이 약관은 {formatTermsDate(version.effectiveDate)}부터 {formatTermsDate(nextVersion.effectiveDate)} 전까지
              적용된 이전 버전입니다.
            </p>
            <button
              type="button"
              className="mt-1 text-primary underline underline-offset-2"
              onClick={() => handleVersionChange(0)}
            >
              현재 약관 보기
            </button>
          </div>
        )}

        {/* 설명 */}
        <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
          본 약관은 서비스 제공자가 제공하는 TOBO 서비스(이하 &quot;서비스&quot;)에 대한 서비스 제공자와 사용자
          여러분(이하 &quot;사용자&quot;)의 이용에 대한 권리와 의무, 책임 사항을 담고 있습니다. 이용약관을 위반하는 경우
          서비스 이용에 제한이 있을 수 있으니 서비스 이용 전 시간을 내어 이용약관을 숙지해주시기 바랍니다.
        </p>

        {/* 이용약관 내용 */}
        <div className="flex flex-col gap-6">
          {version.sections.map(({ title, description, content }) => (
            <section key={title}>
              <h2 className="mb-2 text-lg font-bold">{title}</h2>
              <hr className="mb-4 border-border" />
              <div className="flex flex-col gap-3 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul_li]:py-1 [&_ul_ul]:list-[circle]">
                {description && <p>{description}</p>}
                <ul>
                  {content?.map(item =>
                    typeof item === "string" ? (
                      <li key={item}>{item}</li>
                    ) : (
                      <ul key={item.join(",")}>
                        {item?.map(detail => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    ),
                  )}
                </ul>
              </div>
            </section>
          ))}
        </div>

        {/* 문의처 */}
        <div className="mt-10 text-sm leading-relaxed">
          <p>
            서비스 이용에 관한 문의:{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        {/* 시행일·개정 이력 */}
        <div className="mt-4 flex flex-col gap-1 text-sm text-muted-foreground">
          {version.announcedDate && <p>공고일자: {formatTermsDate(version.announcedDate)}</p>}
          <p>이 약관은 {formatTermsDate(version.effectiveDate)}부터 시행합니다.</p>
          {version.changeSummary && <p>개정 요지: {version.changeSummary}</p>}

          {isLatest && TERMS_VERSIONS.length > 1 && (
            <button
              type="button"
              className="mt-2 self-start text-primary underline underline-offset-2"
              onClick={() => handleVersionChange(1)}
            >
              이전 약관 보기 ({formatTermsDate(TERMS_VERSIONS[1].effectiveDate)} 시행)
            </button>
          )}
        </div>
      </main>
    </Screen>
  );
}
